import { ButtonSize } from '@nx-frontend/shared/ui/button';
import {
  DialogPopupPayload,
  Org,
  OrgInfoSubscriptionType,
  PaywallPopupReason,
  PopupType,
  User,
} from '@nx-frontend/shared/types';
import { OpenModalFn } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { convertBytes } from '@nx-frontend/shared/utils';

interface ShowStoragetSizeLimitPopupParams {
  openModal: OpenModalFn;
  subscriptionType: OrgInfoSubscriptionType;
  currentOrganization: Org;
  currentUser: User;
  getStorageUsage: () => Promise<{ currentStorage: number; maxStorage: number }>;
  attachmentSize: number;
  attachmentFileName: string;
  isMaxSubscriptionPlan: boolean;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

/**
 * Shows limit popup when storage size limit is reached
 * @param params
 */
export async function showStorageSizeLimitPopup(params: ShowStoragetSizeLimitPopupParams): Promise<void> {
  const {
    openModal,
    currentOrganization,
    currentUser,
    attachmentSize,
    subscriptionType,
    isMaxSubscriptionPlan,
    getStorageUsage,
    getOpenPaywallPopup,
  } = params;

  const { maxStorage, currentStorage } = await getStorageUsage();

  const maxSize = convertBytes(maxStorage);

  let text: DialogPopupPayload['text'] = '';
  let buttons: DialogPopupPayload['buttons'] = [
    {
      main: false,
      size: ButtonSize.MD,
      text: 'Ok',
    },
    {
      main: true,
      size: ButtonSize.MD,
      text: 'Upgrade',
      onClick: () => {
        getOpenPaywallPopup({
          openModal,
          payload: {
            payloadType: PopupType.PAYWALL,
            mode: 'upgrade',
            title: 'Allow yourself more: store all important files in one place with our advanced subscriptions!',
            reason: PaywallPopupReason.STORAGET_SIZE_LIMIT,
            orgId: currentOrganization.globalId,
            subscriptionType,
          },
        });
      },
    },
  ];
  const maxPlanDetailText = (
    <>
      Please email{' '}
      <a href="mailto:contact@thefusebase.com" className="text-cyan-700">
        contact@thefusebase.com
      </a>{' '}
      to request an increase in your storage limit
    </>
  );
  if (isMaxSubscriptionPlan) {
    buttons = [
      {
        main: true,
        size: ButtonSize.MD,
        text: 'Ok',
      },
    ];
  }

  if (currentOrganization.userId !== currentUser.id) {
    text = (
      <div>
        You cannot upload the file because it exceeds the available storage space:
        <ul className="list-disc mt-4 mb-4 ml-4">
          <li>
            <strong>{maxSize}</strong>
          </li>
        </ul>
        Please contact the organization owner for more details.
      </div>
    );
    buttons = [
      {
        main: true,
        size: ButtonSize.MD,
        text: 'Ok',
      },
    ];
  } else if (currentStorage >= maxStorage) {
    text = (
      <div>
        You have reached the storage limit for your plan:
        <ul className="list-disc mt-4 mb-4 ml-4">
          <li>
            <strong>{maxSize}</strong>
          </li>
        </ul>
        {isMaxSubscriptionPlan ? (
          maxPlanDetailText
        ) : (
          <>You can delete unnecessary content or upgrade to a plan with more storage.</>
        )}
      </div>
    );
  } else if (attachmentSize + currentStorage > maxStorage) {
    text = (
      <div>
        You cannot upload the file because it exceeds the available storage space:
        <ul className="list-disc mt-4 mb-4 ml-4">
          <li>
            <strong>{maxSize}</strong>
          </li>
        </ul>
        {isMaxSubscriptionPlan ? (
          maxPlanDetailText
        ) : (
          <>Please choose a different file or upgrade to a plan with more storage.</>
        )}
      </div>
    );
  }

  openModal({
    title: 'Storage limit reached',
    content: PopupType.DIALOG,
    closeButtonSize: 18,
    payload: {
      payloadType: PopupType.DIALOG,
      text,
      buttons,
    },
  });
  return;
}
